import { useState } from 'react';
import AdminLayout from '@/components/feature/AdminLayout';
import { ErrorState, EmptyState } from '@/components/feature/AsyncState';
import { useProducts } from '@/hooks/useProducts';
import { useInventory } from '@/hooks/useInventory';
import { useCategories } from '@/hooks/useCategories';
import ProductModal from './components/ProductModal';
import CategoriesModal from './components/CategoriesModal';
import type { Product } from '@/services/products';

export default function ProductsPage() {
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const { data: products, loading, error, refresh } = useProducts();
  const { inventory, refetch: refetchInventory } = useInventory();
  const { data: categories, refresh: refreshCategories } = useCategories();
  const [editing, setEditing] = useState<Product | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [showCategories, setShowCategories] = useState(false);

  const filtered = products.filter((p) => {
    if (catFilter !== 'all' && p.categoryId !== catFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!p.name.toLowerCase().includes(q) && !p.sku.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  return (
    <AdminLayout>
      <div className="p-4 lg:p-6 space-y-5 max-w-[1400px] mx-auto">
        {error && <ErrorState message={error} onRetry={refresh} />}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-foreground-950">Productos</h1>
            <p className="text-sm text-foreground-500 mt-0.5 flex items-center flex-wrap gap-2">
              <span>{products.length} referencias en catálogo</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium whitespace-nowrap">
                Supabase
              </span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowCategories(true)}
              className="text-sm px-4 py-2.5 border border-background-300 text-foreground-700 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap font-medium cursor-pointer"
            >
              <i className="ri-folder-line mr-1.5"></i>Categorías
            </button>
            <button
              onClick={() => setShowNew(true)}
              className="text-sm px-4 py-2.5 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors whitespace-nowrap font-medium cursor-pointer"
            >
              <i className="ri-add-line mr-1.5"></i>Nuevo Producto
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex flex-wrap gap-1.5">
            <button
              onClick={() => setCatFilter('all')}
              className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors whitespace-nowrap cursor-pointer ${
                catFilter === 'all'
                  ? 'bg-accent-500 text-white'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              Todas
            </button>
            {categories.map((c) => (
              <button
                key={c.id}
                onClick={() => setCatFilter(c.id)}
                className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  catFilter === c.id
                    ? 'bg-accent-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>
          <div className="relative flex-1 max-w-xs ml-auto">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input
              type="text"
              placeholder="Buscar producto..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-700 focus:outline-none focus:ring-2 focus:ring-accent-300"
            />
          </div>
        </div>

        {/* Product Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-xl border border-background-200 p-4 animate-pulse"
              >
                <div className="aspect-square bg-background-100 rounded-lg mb-3"></div>
                <div className="h-3 bg-background-100 rounded w-2/3 mb-2"></div>
                <div className="h-3 bg-background-100 rounded w-1/3"></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filtered.map((product) => {
              const cat = categories.find((c) => c.id === product.categoryId);
              const productInventory = inventory.filter((i) => i.productId === product.id);
              const totalStock = productInventory.reduce((s, i) => s + i.quantityAvailable, 0);
              return (
                <button
                  key={product.id}
                  onClick={() => setEditing(product)}
                  className="bg-white rounded-xl border border-background-200 overflow-hidden hover:border-accent-300 transition-all group text-left w-full cursor-pointer"
                >
                  <div className="aspect-square bg-background-100 relative overflow-hidden">
                    {product.images?.[0] ? (
                      <img
                        src={product.images[0]}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <i className="ri-image-line text-foreground-300 text-4xl"></i>
                      </div>
                    )}
                    {!product.active && (
                      <div className="absolute top-3 left-3 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-medium">
                        Inactivo
                      </div>
                    )}
                    {totalStock === 0 && product.active && (
                      <div className="absolute top-3 left-3 bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full font-medium">
                        Agotado
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <p className="text-xs text-foreground-500 mb-0.5">{cat?.name || 'Sin categoría'}</p>
                    <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-accent-600 transition-colors">
                      {product.name}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-0.5 truncate">{product.sku}</p>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-background-100">
                      <span className="text-sm font-bold text-foreground-950">
                        ${product.commercialPrice.toLocaleString()}
                      </span>
                      <span
                        className={`text-xs font-medium ${
                          totalStock > 0 ? 'text-emerald-600' : 'text-red-500'
                        }`}
                      >
                        {totalStock > 0 ? `${totalStock} en stock` : 'Sin stock'}
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {!loading && !error && filtered.length === 0 && <EmptyState />}

      {(editing || showNew) && (
        <ProductModal
          product={editing}
          categories={categories}
          onClose={() => {
            setEditing(null);
            setShowNew(false);
          }}
          onSaved={() => {
            refresh();
            refetchInventory();
          }}
        />
      )}

      {showCategories && (
        <CategoriesModal
          onClose={() => setShowCategories(false)}
          onChanged={refreshCategories}
        />
      )}
    </AdminLayout>
  );
}